import { useState } from 'react';
import './RightSidebar.styles.css';
import { Button, Typography } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { ConfirmModal } from '../../../../components/confirm-modal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LuOctagonAlert, LuSection } from 'react-icons/lu';

import {
  approveDeleteRegulation,
  rejectDeleteRegulation,
  requestDeleteRegulation,
} from '../../Regulation.services.tsx';
import { useRegulation } from '../../useRegulation';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../../hooks/usePermissions.ts';
import { RiShareForwardLine } from 'react-icons/ri';
import { FiCheckSquare, FiCompass, FiEdit, FiUsers } from 'react-icons/fi';
import { MdOutlineArrowOutward } from 'react-icons/md';
import { DATE_FORMAT } from '../../../../constants/app.ts';
import { useAuth } from '../../../../context/AuthContext.tsx';
import { orderBy } from 'lodash';
import classnames from 'classnames';
import { REGULATION_TYPE } from '../../../../constants/regulation-type.tsx';

const { Title, Text } = Typography;

const getIcon = ({ type }: { type: number }) => {
  switch (type) {
    case 1: {
      return <LuSection style={{ fontSize: '20px' }} />;
    }
    case 2: {
      return <FiUsers style={{ fontSize: '20px' }} />;
    }
    case 3: {
      return <FiCompass style={{ fontSize: '20px' }} />;
    }
    default: {
      return;
    }
  }
};

export function RightSidebar() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { isAdmin, isAuditor, isRegularUser } = usePermissions();
  const [showConfirmDeletion, setShowConfirmDeletion] = useState(false);
  const [showApproveDeletion, setShowApproveDeletion] = useState(false);
  const [showRejectDeletion, setShowRejectDeletion] = useState(false);

  const { id, data } = useRegulation();
  const { isAllowed } = usePermissions(data?.type);
  const isRequest = location.pathname.includes('request-deletion');

  const requestDeleteMutation = useMutation({
    mutationFn: requestDeleteRegulation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draftRegulation'] });
    },
    onError: () => {
      console.error('Failed to request deletion');
    },
  });

  const approveDeleteMutation = useMutation({
    mutationFn: approveDeleteRegulation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draftRegulation'] });
      navigate(`/regulation/${data.id}`);
    },
    onError: () => {
      console.error('Failed to request deletion');
    },
  });

  const rejectDeleteMutation = useMutation({
    mutationFn: rejectDeleteRegulation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draftRegulation'] });
      navigate(`/regulation/${data.id}`);
    },
    onError: () => {
      console.error('Failed to request deletion');
    },
  });

  const handleCloseConfirmDeletion = () => setShowConfirmDeletion(false);
  const handleCloseApproveDeletion = () => setShowApproveDeletion(false);
  const handleCloseRejectDeletion = () => setShowRejectDeletion(false);

  const handleConfirmDeletion = (message: string) => {
    requestDeleteMutation.mutate({ id, data: { deletionMessage: message } });
    setShowConfirmDeletion(false);
  };

  const handleApproveDeletion = (
    message: string,
    subject: string,
    groups: number[],
    notifications: boolean,
  ) => {
    if (!id) return;
    approveDeleteMutation.mutate({
      id,
      data: {
        requestMessage: message,
        title: notifications ? subject : null,
        distributionLists: notifications ? groups.map((item) => Number(item)) : null,
      },
    });
    setShowApproveDeletion(false);
  };

  const handleRejectDeletion = () => {
    rejectDeleteMutation.mutate({ id });
    setShowConfirmDeletion(false);
  };

  const isInLeadership = data?.unitInCharge?.leadership?.includes(user?.id);

  const canEdit =
    (!isRequest && id && isRegularUser && !data?.isDeleted && data?.isActive) ||
    (!isRequest && id && isAdmin && !data?.isDeleted && data?.isActive) ||
    (isRequest && id && isAllowed && !data?.isDeleted && data?.isActive);
  const canDelete =
    (!isRequest && id && isRegularUser && !data?.isDeleted && data?.isActive) ||
    (!isRequest && id && isAdmin && !data?.isDeleted && data?.isActive);
  const canSendViaEmail =
    isRequest && data?.forwardEmails && !isAllowed && !data?.isDeleted && data?.isActive;
  const canApproveDeletion =
    (id && isRequest && isAllowed && !data?.isDeleted && data?.isActive) ||
    (id && isRequest && isAdmin && !data?.isDeleted && data?.isActive) ||
    (id && isRequest && isInLeadership && data?.type === 3 && !data?.isDeleted && data?.isActive);
  // const canRejectDeletion =
  //   (id && isRequest && isAllowed && !data?.isDeleted && data?.isActive) ||
  //   (id && isRequest && isAdmin && !data?.isDeleted && data?.isActive) ||
  //   (id && isRequest && isInLeadership && data?.type === 3 && !data?.isDeleted && data?.isActive);

  if (data?.isDeleted) {
    const mainLayoutCls = document.querySelector('.layout-main-content')?.classList;
    if (mainLayoutCls) {
      mainLayoutCls.remove('two-sidebars');
      mainLayoutCls.add('one-sidebar');
    }
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 12,
        width: 300,
        height: '100%',
      }}
    >
      <div className="draft-right-sidebar-container">
        <Title
          style={{ padding: 0, margin: 0, display: 'flex', gap: 4, alignItems: 'center' }}
          level={4}
        >
          {getIcon({ type: data?.type })}
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-expect-error */}
          {REGULATION_TYPE[data?.type] as 1 | 2 | 3}
        </Title>
        <div className="action-links">
          {canEdit && (
            <div
              className="action-link"
              onClick={() => navigate(`/regulation/draft/${data?.regulationDraftId}`)}
            >
              <FiEdit fontSize={18} />
              <span className="action-link-text">{t('common.edit')}</span>
            </div>
          )}
          {canDelete && (
            <div className="action-link" onClick={() => setShowConfirmDeletion(true)}>
              <FiCheckSquare fontSize={18} />
              <span className="action-link-text">{t('common.delete-regulation')}</span>
            </div>
          )}
          {canApproveDeletion && (
            <div
              style={{
                borderRadius: 16,
                background: '#F67C000F',
                padding: '16px 8px',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <LuOctagonAlert fontSize={20} color="#F67C00" />
                <span style={{ fontSize: 16, fontWeight: 500 }}>
                  {t('common.deletion-request')}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="primary" onClick={() => setShowApproveDeletion(true)}>
                  {t('common.approve')}
                </Button>
                <Button color="danger" variant="solid" onClick={handleRejectDeletion}>
                  {t('common.decline')}
                </Button>
              </div>
            </div>
          )}
          {canSendViaEmail && (
            <div
              className="action-link"
              onClick={() => {
                const recipients = Array.isArray(data.forwardEmails)
                  ? data.forwardEmails.join(',')
                  : String(data.forwardEmails);

                const subject = encodeURIComponent(data.title || 'Regulation Draft');
                const url = `${window.location.origin}/regulation/draft/view/${data.id}`;
                const body = encodeURIComponent(`Link to the regulation:\n${url}`);
                window.location.href = `mailto:${recipients}?subject=${subject}&body=${body}`;
              }}
            >
              <RiShareForwardLine fontSize={18} />
              <span className="action-link-text">{t('common.forward')}</span>
            </div>
          )}
          {/*{*/}
          {/*  <div className="action-link" onClick={() => navigate(-1)}>*/}
          {/*    <FiX fontSize={18} />*/}
          {/*    <span className="action-link-text">{t('common.cancel')}</span>*/}
          {/*  </div>*/}
          {/*}*/}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data?.regulationNumber && (
              <Text type="secondary" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="action-link-label">{t('common.number')}:</span>
                <span className="action-link-text">{data?.regulationNumber}</span>
              </Text>
            )}
            {data?.creationDate && (
              <Text type="secondary" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="action-link-label">{t('common.creation-date')}:</span>{' '}
                <span className="action-link-text">
                  {dayjs(data?.creationDate).format('DD.MM.YYYY')}
                </span>
              </Text>
            )}
            {data?.modifiedAt && (
              <Text type="secondary" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="action-link-label">{t('common.edited')}:</span>{' '}
                <span className="action-link-text">
                  {dayjs(data?.modifiedAt).format('DD.MM.YYYY')}
                </span>
              </Text>
            )}
            {data?.confirmationDate && (
              <Text type="secondary" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="action-link-label">{t('common.expiration')}:</span>{' '}
                <span className="action-link-text">
                  {dayjs(data?.confirmationDate).format('DD.MM.YYYY')}
                </span>
              </Text>
            )}
            {data?.unitInCharge && (
              <Text
                type="secondary"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  gap: 4,
                }}
              >
                <span className="action-link-text" style={{ textAlign: 'left' }}>
                  {t('common.responsible')}:
                </span>
                <span className="action-link-text" style={{ textAlign: 'left' }}>
                  {data?.unitInCharge?.title}
                </span>
              </Text>
            )}
            {data?.involvedUnits?.length > 0 && (
              <Text
                type="secondary"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  gap: 4,
                }}
              >
                <span className="action-link-text" style={{ textAlign: 'left' }}>
                  {t('common.in-collaboration')}:
                </span>
                <span className="action-link-text" style={{ textAlign: 'left' }}>
                  {data?.involvedUnits?.map((unit: { title: string }) => unit.title).join(', ')}
                </span>
              </Text>
            )}
            {data?.emergedOut && (
              <Text
                type="secondary"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  gap: 4,
                }}
              >
                <span className="action-link-label" style={{ textAlign: 'left' }}>
                  {t('form.field.emerged-out')}:
                </span>
                <span className="action-link-text" style={{ textAlign: 'left' }}>
                  {data?.emergedOut}
                </span>
              </Text>
            )}
            {data?.publicationDate && !data?.isActive && (
              <Text type="secondary" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="action-link-label">{t('common.in-force')}:</span>{' '}
                <span className="action-link-text">
                  {dayjs(data?.publicationDate).format('DD.MM.YYYY')}
                </span>
              </Text>
            )}
          </div>
        </div>
        {showConfirmDeletion && (
          <ConfirmModal
            open={showConfirmDeletion}
            simple
            helplink
            validateMessage
            title={t('modals.confirm-request-deletion-3')}
            message={t('modals.confirm-request-deletion-message-3')}
            onClose={handleCloseConfirmDeletion}
            onConfirm={handleConfirmDeletion}
          />
        )}
        {showRejectDeletion && (
          <ConfirmModal
            open={showRejectDeletion}
            title={t('modals.confirm-reject-deletion')}
            simple
            message={t('modals.confirm-reject-deletion-message')}
            onClose={handleCloseRejectDeletion}
            onConfirm={handleRejectDeletion}
          />
        )}
        {showApproveDeletion && (
          <ConfirmModal
            regulationDraftId={data?.regulationDraftId}
            open={showApproveDeletion}
            templateType="delete"
            loadTemplate
            title={t('modals.confirm-approve-deletion')}
            subject={data?.title}
            message={t('modals.confirm-approve-deletion-message')}
            onClose={handleCloseApproveDeletion}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            onConfirm={handleApproveDeletion}
          />
        )}
      </div>
      {(isAdmin || isAuditor) && (
        <div className="draft-right-sidebar-container">
          <Title style={{ padding: 0, margin: 0 }} level={5}>
            {t('common.history')}
          </Title>
          <div>
            {data?.versions.length > 0
              ? orderBy(data?.versions, 'publicationDate', 'desc')?.map(
                  (
                    version: { id: number; title: string; publicationDate: string },
                    index: number,
                  ) => (
                    <>
                      <Link
                        key={version.id}
                        to={
                          data?.versions.length === 1
                            ? '#'
                            : `/regulation/${version.id}?history=true`
                        }
                      >
                        <div
                          className={classnames('draft-link', {
                            disabled: data?.versions.length === 1,
                          })}
                        >
                          {dayjs(version.publicationDate).format(DATE_FORMAT)}
                          <MdOutlineArrowOutward color="var(--primary-color)" />
                        </div>
                      </Link>
                      {!data?.isActive && index === data?.versions.length - 1 && (
                        <Link
                          key={version.id}
                          to={`/regulation/${orderBy(data?.versions, 'publicationDate', 'desc')[0].id}`}
                        >
                          <div className="draft-link">
                            {t('common.last-version')}
                            <MdOutlineArrowOutward color="var(--primary-color)" />
                          </div>
                        </Link>
                      )}
                    </>
                  ),
                )
              : null}
          </div>
        </div>
      )}
    </div>
  );
}
